/**
 * Shared API service for KuzFit API calls
 */

const API_BASE_URL = `${process.env.REACT_APP_API_SERVER_URL || 'https://api.kuzfit.com'}/kuzfit`;

/**
 * Whether the shop key's merchant owns this page origin (the connect popup and the session frame
 * ask before they hand a shopper's access token to the page).
 * @returns {Promise<boolean>}
 */
export const checkShopOrigin = async (shopKey, origin) => {
  const query = `shopKey=${encodeURIComponent(shopKey)}&origin=${encodeURIComponent(origin)}`;
  const response = await fetch(`${API_BASE_URL}/shopOrigin?${query}`, { method: 'GET' });
  return response.ok;
};

/**
 * Fetch all photos from KuzFit API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response with photo URLs
 */
export const fetchAllPhotos = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/photoGetAll`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoGetAll failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * The signed-in shopper's selfie versions; read the response with readSelfies (connectFlow).
 * @returns {Promise<any>}
 */
export const fetchSelfies = async (accessToken) => {
  const response = await fetch(`${API_BASE_URL}/selfies`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  if (!response.ok) {
    throw new Error(`selfies failed: ${response.status}`);
  }
  return response.json();
};

/**
 * Makes one of the shopper's earlier selfies the one try-ons use again.
 * @returns {Promise<any>} { active, version }
 */
export const activateSelfie = async (fileId, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/selfieActivate?fileId=${encodeURIComponent(fileId)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  if (!response.ok) {
    throw new Error(`selfieActivate failed: ${response.status}`);
  }
  return response.json();
};

/**
 * The signed-in shopper's own choices; read the response with readPreferences (connectFlow).
 * @returns {Promise<any>}
 */
export const fetchPreferences = async (accessToken) => {
  const response = await fetch(`${API_BASE_URL}/preferences`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  if (!response.ok) {
    throw new Error(`preferences failed: ${response.status}`);
  }
  return response.json();
};

/**
 * Saves whether the shopper wants automatic try-ons in every KuzFit shop.
 * @returns {Promise<any>} { autoTryOn, updatedUtc }
 */
export const savePreferences = async (autoTryOn, accessToken) => {
  const response = await fetch(`${API_BASE_URL}/preferences`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ autoTryOn })
  });
  if (!response.ok) {
    throw new Error(`saving preferences failed: ${response.status}`);
  }
  return response.json();
};

/**
 * Delete a photo via KuzFit API
 * @param {string} photoId - ID of the photo to delete
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const photoDelete = async (photoId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/photoDelete?fileId=${photoId}`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoDelete failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Upload photo to KuzFit API
 * @param {File} file - File to upload
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @param {Array<string>} params - optional parameters array
 * @returns {Promise<any>} Parsed response
 */
export const photoUpload = async (file, accessToken, params = []) => {
  const headers = {
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const formData = new FormData();
  formData.append('Files', file);
  
  // Add params if provided
  params.forEach(param => {
    formData.append('Params', param);
  });

  const response = await fetch(`${API_BASE_URL}/photoUpload`, {
    method: 'POST',
    headers,
    body: formData
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoUpload failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Get image content from KuzFit API
 * @param {number} fileId - ID of the file to retrieve
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<Blob>} Image blob data
 */
export const getImage = async (fileId, accessToken) => {
  const headers = {
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/getImage?id=${fileId}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`getImage failed: ${response.status} ${text}`);
  }

  return response.blob();
};
